<div class="container mx-auto space-y-8 px-4 py-10">
  @if (isLoading) {
    <div class="grid items-center gap-8 lg:grid-cols-2">
      <div class="space-y-3">
        <hlm-skeleton class="h-10 w-3/4" />
        <hlm-skeleton class="h-5 w-full" />
        <hlm-skeleton class="h-5 w-2/3" />
        <hlm-skeleton class="mt-4 h-9 w-48" />
      </div>
      <hlm-skeleton class="hidden aspect-square w-full max-w-md justify-self-center lg:block" />
    </div>
  }

  @if (hasError) {
    <div hlmAlert variant="destructive" role="alert">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>Deine Übersicht konnte nicht geladen werden</p>
      <p hlmAlertDescription>Bitte prüfe deine Internetverbindung und versuche es erneut.</p>
      <button hlmBtn variant="outline" size="sm" class="mt-2" type="button" (click)="retry()">
        Erneut versuchen
      </button>
    </div>
  }

  @if (!isLoading && !hasError && company) {
    <!-- ═══ Hero ═══ -->
    <section class="grid items-center gap-8 lg:grid-cols-2">
      <div class="space-y-4">
        <div>
          <h1 hlmH3>{{ greeting }}</h1>
          <p hlmLead class="mt-2">{{ intro }}</p>
        </div>

        <!-- Sales at a glance, once there is something to show (a Zentrale sees its
             network's numbers in the network card instead) -->
        @if (pageMode === 'active' && statistics && !ownNetwork) {
          <div class="grid grid-cols-2 gap-3 sm:max-w-md">
            <app-kpi-card
              accent="default"
              label="Verkaufte Gutscheine"
              [value]="(statistics.total_vouchers_sold || 0).toString()">
              <ng-template #icon><ng-icon name="lucideTicket" /></ng-template>
            </app-kpi-card>
            <app-kpi-card
              accent="default"
              label="Eingelöste Gutscheine"
              [value]="(statistics.total_vouchers_redeemed || 0).toString()">
              <ng-template #icon><ng-icon name="lucideTicketCheck" /></ng-template>
            </app-kpi-card>
          </div>
        }

        <!-- The member's network and its own standing in it, right at the top -->
        @if (company.is_member && network; as net) {
          <div hlmCard data-size="sm">
            <div hlmCardContent class="space-y-3">
              <div class="flex items-start gap-3">
                <hlm-avatar class="size-12 rounded-lg">
                  @if (imageUrl(net.central_company_logo_image_path); as url) {
                    <img hlmAvatarImage class="object-contain" [src]="url" [alt]="net.name" />
                  }
                  <span hlmAvatarFallback class="rounded-lg">{{ initials(net.name) }}</span>
                </hlm-avatar>
                <div class="min-w-0 flex-1">
                  <p class="font-semibold">{{ net.name }}</p>
                  @if (net.central_company_address) {
                    <p hlmMuted class="flex items-center gap-1 text-sm">
                      <ng-icon name="lucideMapPin" class="shrink-0" />
                      <span class="truncate">{{ net.central_company_address }}</span>
                    </p>
                  }
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <span class="flex items-center gap-2">
                  <span
                    hlmBadge
                    [variant]="
                      myStatus === 'active'
                        ? 'solid'
                        : myStatus === 'ended'
                          ? 'destructive'
                          : 'outline'
                    ">
                    {{ myStatusLabel }}
                  </span>
                  @if (myStatus === 'active' && mySince) {
                    <span hlmMuted>seit {{ mySince }}</span>
                  }
                </span>
                <span class="flex items-center gap-1.5">
                  <ng-icon name="lucideLandmark" class="text-muted-foreground shrink-0" />
                  @if (company.has_bank_account) {
                    <span hlmMuted>Auszahlung eingerichtet</span>
                  } @else {
                    <a
                      hlmBadge
                      variant="destructive"
                      class="hover:opacity-90"
                      [routerLink]="['/settings']"
                      [queryParams]="{ tab: 'bank-account' }"
                      >IBAN fehlt</a
                    >
                  }
                </span>
              </div>

              <div class="flex flex-wrap gap-2">
                @if (myStatus === 'incomplete') {
                  <a hlmBtn size="sm" routerLink="/onboarding">Registrierung abschließen</a>
                }
                <a
                  hlmBtn
                  size="sm"
                  [variant]="myStatus === 'incomplete' ? 'outline' : 'default'"
                  [routerLink]="['/settings']"
                  [queryParams]="{ tab: 'verbund-members' }">
                  <ng-icon name="lucideUsers" />
                  Zum Netzwerk
                </a>
                <a hlmBtn variant="outline" size="sm" routerLink="/payouts">
                  <ng-icon name="lucideWallet" />
                  Auszahlungen
                </a>
                @if (centralStorefrontUrl) {
                  <a
                    hlmBtn
                    variant="outline"
                    size="sm"
                    [href]="centralStorefrontUrl"
                    target="_blank"
                    rel="noopener">
                    <ng-icon name="lucideStore" />
                    Netzwerk-Shop öffnen
                  </a>
                }
              </div>
            </div>
          </div>
        }

        <!-- The network this company runs, with this month's numbers -->
        @if (ownNetwork; as net) {
          <div hlmCard data-size="sm">
            <div hlmCardContent class="space-y-4">
              <div class="flex items-start gap-3">
                <hlm-avatar class="size-12 rounded-lg">
                  @if (imageUrl(net.central_company_logo_image_path); as url) {
                    <img hlmAvatarImage class="object-contain" [src]="url" [alt]="net.name" />
                  }
                  <span hlmAvatarFallback class="rounded-lg">{{ initials(net.name) }}</span>
                </hlm-avatar>
                <div class="min-w-0 flex-1">
                  <p class="flex flex-wrap items-center gap-2 font-semibold">
                    {{ net.name }}
                    <span hlmBadge variant="secondary">Zentrale</span>
                  </p>
                  @if (net.central_company_address) {
                    <p hlmMuted class="flex items-center gap-1 text-sm">
                      <ng-icon name="lucideMapPin" class="shrink-0" />
                      <span class="truncate">{{ net.central_company_address }}</span>
                    </p>
                  }
                </div>
              </div>

              <dl class="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                <div>
                  <dt hlmMuted class="text-xs">Mitglieder</dt>
                  <dd class="text-lg font-semibold tabular-nums">{{ activeMemberCount(net) }}</dd>
                </div>
                @if (networkMonth; as month) {
                  <div>
                    <dt hlmMuted class="text-xs">Verkauft im {{ month.period_label }}</dt>
                    <dd class="text-lg font-semibold tabular-nums">
                      {{ money(month.sales.face_value_cents) }}
                    </dd>
                  </div>
                  <div>
                    <dt hlmMuted class="text-xs">Bei Mitgliedern eingelöst</dt>
                    <dd class="text-lg font-semibold tabular-nums">
                      {{ money(month.member_redemptions_total.value_cents) }}
                    </dd>
                  </div>
                  <div>
                    <dt hlmMuted class="text-xs">An Mitglieder zu überweisen</dt>
                    <dd class="text-lg font-semibold tabular-nums">
                      {{ money(month.member_payout_total_cents) }}
                    </dd>
                  </div>
                }
              </dl>

              @if (pendingMemberCount(net) > 0) {
                <div hlmAlert>
                  <ng-icon name="lucideUsers" />
                  <p hlmAlertTitle>
                    {{ pendingMemberCount(net) }}
                    {{
                      pendingMemberCount(net) === 1
                        ? 'Registrierung wartet'
                        : 'Registrierungen warten'
                    }}
                    auf deine Freigabe
                  </p>
                </div>
              }

              <div class="flex flex-wrap gap-2">
                <a
                  hlmBtn
                  size="sm"
                  [routerLink]="['/settings']"
                  [queryParams]="{ tab: 'verbund-members' }">
                  <ng-icon name="lucideUsers" />
                  Mitglieder
                </a>
                <a
                  hlmBtn
                  variant="outline"
                  size="sm"
                  [routerLink]="['/settings']"
                  [queryParams]="{ tab: 'verbund-settlements' }">
                  <ng-icon name="lucideWallet" />
                  Abrechnungen
                </a>
              </div>
            </div>
          </div>
        }

        <div class="flex flex-wrap gap-2">
          @if (company.is_member) {
            <!-- The member's actions live in the network card above -->
          } @else if (pageMode === 'active' && !ownNetwork) {
            <a hlmBtn [routerLink]="['/voucher-dashboard']">
              <ng-icon name="lucideChartNoAxesColumn" />
              Alle Statistiken
            </a>
          } @else if (pageMode === 'onboarding') {
            <a hlmBtn [routerLink]="['/settings']" [queryParams]="{ tab: 'payments' }">
              <ng-icon name="lucideCreditCard" />
              Zahlungen einrichten
            </a>
          }
        </div>
      </div>

      <div class="flex justify-center lg:justify-end">
        <img class="w-full max-w-md" src="./assets/illustration.svg" alt="" />
      </div>
    </section>

    <!-- ═══ Setup checklist (members get their own, shorter list) ═══ -->
    <app-setup-checklist class="block max-w-2xl" />
  }
</div>
